import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { OrdersService, UsersService } from '@/services/firebase.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, User, ShoppingBag, Menu, X } from 'lucide-react';
import CartDrawer from '@/components/CartDrawer';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, getCartCount, setIsCartOpen } = useCart();
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    houseNo: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
  });
  const cartCount = getCartCount();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      toast.info('Please login to proceed with checkout');
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Auto-fill from user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        try {
          const profile = await UsersService.getByUserId(currentUser.uid);
          setFormData({
            fullName: profile.name || '',
            phone: profile.phone || '',
            email: profile.email || currentUser.email,
            houseNo: '',
            street: profile.address || '',
            city: profile.city || '',
            state: '',
            pincode: profile.pincode || '',
          });
        } catch (error) {
          console.error('Error loading profile:', error);
          setFormData(prev => ({ ...prev, email: currentUser.email }));
        }
      }
      setProfileLoading(false);
    };
    loadProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (formData.phone.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return false;
    }
    if (formData.pincode.length !== 6) {
      toast.error('Pincode must be 6 digits');
      return false;
    }
    return true;
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      const amount = getCartTotal() * 100; // Convert to paise

      // Razorpay Key ID (public key - safe to use in frontend)
      const RAZORPAY_KEY_ID = 'rzp_live_RyRcSS38wf9O5Q';

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount,
        currency: 'INR',
        name: 'B&C Store',
        description: 'Payment for your order',
        image: 'https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png',
        handler: async function (response) {
          // Payment successful - create order
          try {
            const orderItems = cart.map(item => ({
              productId: item.product.id,
              productTitle: item.product.title,
              title: item.product.title,
              category: item.product.category || 'Uncategorized',
              model: item.product.modelName || item.product.model || '',
              image: item.variant?.image || item.product.images?.[0] || 'https://via.placeholder.com/200',
              price: item.product.price,
              quantity: item.quantity,
              color: item.variant?.color || 'Default',
            }));

            const orderData = {
              items: orderItems,
              customer: {
                name: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                address: `${formData.houseNo} ${formData.street}`,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
              },
              total: getCartTotal(),
              userId: currentUser?.uid || null,
              orderStatus: 'Confirmed',
              paymentId: response.razorpay_payment_id,
              paymentStatus: 'Paid',
            };

            const order = await OrdersService.create(orderData);

            localStorage.setItem('lastOrder', JSON.stringify({
              orderId: order.orderId,
              items: cart,
              total: getCartTotal(),
              customer: formData,
              date: new Date().toISOString(),
              paymentId: response.razorpay_payment_id,
            }));
            
            clearCart();
            toast.success('Payment successful! Order placed.');
            navigate('/thank-you');
          } catch (error) {
            console.error('Error creating order:', error);
            toast.error('Payment received but order creation failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          address: `${formData.houseNo} ${formData.street}, ${formData.city}, ${formData.state} - ${formData.pincode}`,
        },
        theme: {
          color: '#000000',
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <CartDrawer />
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-4">
            <div className="flex items-center justify-between">
              <div className="hidden md:flex items-center space-x-8 w-32"></div>
              <button className="md:hidden text-gray-800" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick}>
                <h1 className="text-xl font-light tracking-[0.2em] text-black hover:text-gray-700 transition-colors">B&C Store</h1>
              </div>
              <div className="flex items-center space-x-5">
                <button onClick={() => navigate('/login')} className="text-gray-700 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
                <button onClick={() => setIsCartOpen(true)} className="text-gray-700 hover:text-black transition-colors relative">
                  <ShoppingBag size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-4">Your cart is empty</h1>
            <p className="text-gray-600 mb-8 font-light">Add some products to proceed with checkout</p>
            <Button onClick={() => navigate('/products')} size="lg" className="bg-black text-white hover:bg-gray-900">
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />
      
      {/* Premium Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center space-x-8 w-32"></div>
            <button className="md:hidden text-gray-800" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick}>
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png" 
                alt="B&C Store" 
                className="h-10 sm:h-12 object-contain hover:opacity-80 transition-opacity"
              />
            </div>
            <div className="flex items-center space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="text-gray-700 hover:text-black transition-colors">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Profile</button>
                      <button onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Orders</button>
                      <button onClick={async () => { await logout(); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light">Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="text-gray-700 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              <button onClick={() => setIsCartOpen(true)} className="text-gray-700 hover:text-black transition-colors relative">
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100">
            <div className="px-6 py-4 space-y-4">
              <button onClick={() => { navigate('/products'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">Shop Now</button>
              <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">My Profile</button>
              <button onClick={() => { navigate('/my-orders'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">My Orders</button>
            </div>
          </motion.div>
        )}
      </nav>

      <div className="pt-20 sm:pt-24 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/products')}
            className="flex items-center text-gray-500 hover:text-black mb-6 sm:mb-8 transition-colors font-light text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Continue Shopping
          </button>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl lg:text-4xl font-light mb-8 sm:mb-12"
          >
            Checkout
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Form Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 sm:p-8 shadow-sm border border-gray-100 rounded-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg sm:text-xl font-light">Shipping Information</h2>
                </div>

                {profileLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border border-gray-300 border-t-black rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <form onSubmit={handlePayment} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-light">Full Name *</Label>
                        <Input
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          className="font-light rounded-lg"
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-light">Phone *</Label>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="font-light rounded-lg"
                          placeholder="9876543210"
                          maxLength={10}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="font-light">Email *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="font-light rounded-lg"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-light">House No./Flat</Label>
                        <Input
                          name="houseNo"
                          value={formData.houseNo}
                          onChange={handleChange}
                          className="font-light rounded-lg"
                          placeholder="Flat 101"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-light">Street/Area</Label>
                        <Input
                          name="street"
                          value={formData.street}
                          onChange={handleChange}
                          className="font-light rounded-lg"
                          placeholder="MG Road"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="font-light">City *</Label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="font-light rounded-lg"
                          placeholder="Mumbai"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-light">State</Label>
                        <Input
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className="font-light rounded-lg"
                          placeholder="Maharashtra"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-light">Pincode *</Label>
                        <Input
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          required
                          className="font-light rounded-lg"
                          placeholder="400001"
                          maxLength={6}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-black text-white hover:bg-gray-900 py-6 text-base font-light tracking-wide rounded-lg mt-6"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <CreditCard className="w-5 h-5" />
                          Pay ₹{getCartTotal()}
                        </div>
                      )}
                    </Button>

                    <p className="text-xs text-gray-500 text-center font-light mt-4">
                      Secure payment powered by Razorpay
                    </p>
                  </form>
                )}
              </Card>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 sm:p-8 shadow-sm border border-gray-100 rounded-2xl sticky top-28">
                <div className="flex items-center gap-3 mb-6">
                  <ShoppingBag className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg sm:text-xl font-light">Order Summary</h2>
                </div>

                <div className="space-y-4 max-h-[300px] overflow-y-auto mb-6">
                  {cart.map((item, index) => (
                    <div key={index} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                      <img
                        src={item.variant?.image || item.product.images?.[0] || 'https://via.placeholder.com/80'}
                        alt={item.product.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-light text-sm truncate">{item.product.title}</h4>
                        {item.variant?.color && (
                          <p className="text-xs text-gray-500 font-light">Color: {item.variant.color}</p>
                        )}
                        <p className="text-xs text-gray-500 font-light">Qty: {item.quantity}</p>
                        <p className="font-medium text-sm mt-1">₹{item.product.price * item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm font-light">
                    <span className="text-gray-600">Subtotal</span>
                    <span>₹{getCartTotal()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-light">
                    <span className="text-gray-600">Shipping</span>
                    <span className="text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between text-lg font-medium pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span>₹{getCartTotal()}</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
